import Base3DController from './base_3d_controller'

// app/javascript/controllers/otherframe_controller.js
export default class extends Base3DController {
  static targets = [
    ...Base3DController.targets,
    'rightFace', 'leftFace', 'topFace', 'bottomFace', 'backFace',
    'depthSlider', 'colorSelector', 'selector',
  ]

  static values = {
    ...Base3DController.values,
    boxDepth: { type: Number, default: 10 }, // Override default depth
  }

  initialize() {
    super.initialize()

    // Frame-specific color options
    this.colors = {
      black: ['#080808', '#181818'],
      white: ['#f1ebeb', '#f5f5f5'],
      natural: ['#d9bf92', '#d9b98c'],
    }
  }

  childConnect() {
    // Initialize colors
    const color = this.colorSelectorTarget.value
    const colorValue = this.colors[color]
    this.updateColors(colorValue)

    // OtherFrame-specific event listeners
    if (this.hasDepthSliderTarget) {
      this.depthSliderTarget.addEventListener('input', this.handleDepthChange)
    }

    if (this.hasSelectorTarget) {
      this.selectorTarget.addEventListener('click', () => {
        this.colorSelectorTarget.click()
      })
    }

    if (this.hasColorSelectorTarget) {
      this.colorSelectorTarget.addEventListener('change', this.updateFrameColor)
    }
  }

  childDisconnect() {
    if (this.hasDepthSliderTarget) {
      this.depthSliderTarget.removeEventListener('input', this.handleDepthChange.bind(this))
    }

    if (this.hasSelectorTarget) {
      this.selectorTarget.removeEventListener('click', () => {
        this.colorSelectorTarget.click()
      })
    }

    if (this.hasColorSelectorTarget) {
      this.colorSelectorTarget.removeEventListener('change', this.updateFrameColor.bind(this))
    }
  }

  // Override dimension updates for frame box (similar to canvas but with color handling)
  updateChildDimensions() {
    // Set box size

    if (this.hasBoxContainerTarget) {
      this.boxContainerTarget.style.width = `${this.boxWidthValue}px`
      this.boxContainerTarget.style.height = `${this.boxHeightValue}px`
    }

    if (this.hasBoxTarget) {
      this.boxTarget.style.width = `${this.boxWidthValue}px`
      this.boxTarget.style.height = `${this.boxHeightValue}px`
    }

    // Calculate half dimensions for positioning
    const halfWidth = this.boxWidthValue / 2
    const halfHeight = this.boxHeightValue / 2
    const halfDepth = this.boxDepthValue / 2

    // Update front and back faces
    if (this.hasFrontFaceTarget) {
      this.frontFaceTarget.style.width = `${this.boxWidthValue}px`
      this.frontFaceTarget.style.height = `${this.boxHeightValue}px`
      this.frontFaceTarget.style.transform = `translateZ(${halfDepth}px)`
    }

    if (this.hasBackFaceTarget) {
      this.backFaceTarget.style.width = `${this.boxWidthValue}px`
      this.backFaceTarget.style.height = `${this.boxHeightValue}px`
      this.backFaceTarget.style.transform = `rotateY(180deg) translateZ(${halfDepth}px)`
    }

    // Update right and left faces
    if (this.hasRightFaceTarget) {
      this.rightFaceTarget.style.width = `${this.boxDepthValue}px`
      this.rightFaceTarget.style.height = `${this.boxHeightValue}px`
      this.rightFaceTarget.style.transform = `rotateY(90deg) translateZ(${halfWidth}px)`
      this.rightFaceTarget.style.left = `${(this.boxWidthValue - this.boxDepthValue) / 2}px`
      this.rightFaceTarget.style.top = '0'
    }

    if (this.hasLeftFaceTarget) {
      this.leftFaceTarget.style.width = `${this.boxDepthValue}px`
      this.leftFaceTarget.style.height = `${this.boxHeightValue}px`
      this.leftFaceTarget.style.transform = `rotateY(-90deg) translateZ(${halfWidth}px)`
      this.leftFaceTarget.style.left = `${(this.boxWidthValue - this.boxDepthValue) / 2}px`
      this.leftFaceTarget.style.top = '0'
    }

    // Update top and bottom faces
    if (this.hasTopFaceTarget) {
      this.topFaceTarget.style.width = `${this.boxWidthValue}px`
      this.topFaceTarget.style.height = `${this.boxDepthValue}px`
      this.topFaceTarget.style.transform = `rotateX(90deg) translateZ(${halfHeight}px)`
      this.topFaceTarget.style.left = '0'
      this.topFaceTarget.style.top = `${(this.boxHeightValue - this.boxDepthValue) / 2}px`
    }

    if (this.hasBottomFaceTarget) {
      this.bottomFaceTarget.style.width = `${this.boxWidthValue}px`
      this.bottomFaceTarget.style.height = `${this.boxDepthValue}px`
      this.bottomFaceTarget.style.transform = `rotateX(-90deg) translateZ(${halfHeight}px)`
      this.bottomFaceTarget.style.left = '0'
      this.bottomFaceTarget.style.top = `${(this.boxHeightValue - this.boxDepthValue) / 2}px`
    }

    this.scaleToFit()
  }

  scaleToFit() {
    if (!this.hasSceneTarget || !this.hasBoxContainerTarget) return

    const sceneRect = this.sceneTarget.getBoundingClientRect()
    const sceneWidth = sceneRect.width
    const sceneHeight = sceneRect.height

    // Account for padding/margin (10% safety margin)
    const maxWidth = sceneWidth * 0.9
    const maxHeight = sceneHeight * 0.9

    // Get current frame dimensions including depth for 3D calculations
    const frameWidth = this.boxWidthValue
    const frameHeight = this.boxHeightValue
    const frameDepth = this.boxDepthValue

    // Calculate the maximum possible size when rotated (worst case)
    const maxRotatedWidth = Math.sqrt(frameWidth * frameWidth + frameDepth * frameDepth)
    const maxRotatedHeight = Math.sqrt(frameHeight * frameHeight + frameDepth * frameDepth)

    // Calculate scale factor to fit within scene
    const scaleX = maxWidth / maxRotatedWidth
    const scaleY = maxHeight / maxRotatedHeight
    const scale = Math.min(scaleX, scaleY, 1) // Never scale up beyond 100%

    // Apply scale to the box container
    this.boxContainerTarget.style.transform
      = `translate(-50%, -50%) scale(${scale}) rotateX(${this.rotateXValue}deg) rotateY(${this.rotateYValue}deg)`
  }

  // Depth slider handler
  handleDepthChange(e) {
    this.updateBoxDepth(parseInt(e.target.value))
  }

  boxDepthValueChanged() {
    this.updateBoxDimensions(this.initialBoxSize.width, this.initialBoxSize.height)
  }

  updateBoxDepth(depth) {
    this.boxDepthValue = depth
  }

  // Frame color handling
  updateFrameColor = () => {
    // Get the selected color value
    const color = this.colorSelectorTarget.value
    const colorValue = this.colors[color]

    this.updateColors(colorValue)
  }

  updateColors(colors) {
    if (this.hasFrontFaceTarget) {
      this.frontFaceTarget.style.borderColor = colors.join(' ')
    }
    if (this.hasRightFaceTarget) {
      this.rightFaceTarget.style.backgroundColor = colors[0]
    }
    if (this.hasLeftFaceTarget) {
      this.leftFaceTarget.style.backgroundColor = colors[0]
    }
    if (this.hasBottomFaceTarget) {
      this.bottomFaceTarget.style.backgroundColor = colors[1]
    }
    if (this.hasTopFaceTarget) {
      this.topFaceTarget.style.backgroundColor = colors[1]
    }
  }
}
